import {memo, useEffect, useMemo, useState} from 'react'
import {Center, type FontData} from '@react-three/drei'
import * as THREE from 'three'
import {FontLoader, TextGeometry, type TextGeometryParameters} from 'three-stdlib'
import {mergeGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import type {SceneText3D} from '../../types/text3dTypes'
import {resolveFontUrl} from '../../types/text3dTypes'
import type {TextAlign} from '../../types/textTypes'
import {getLoadedText3DFont, loadText3DFont, trackText3DFontPending} from '../../utils/text3dFontLoader'
import {reportError} from '../../errors/reportError'

type Text3DGeometryParams = TextGeometryParameters & {bevelSegments?: number}

// Advance width of a string in world units — replicates TextGeometry's
// internal pen positions (offsetX += glyph.ha * scale; letterSpacing is 0),
// so alignment math is immune to bevel padding. Missing glyphs mirror
// FontLoader's fallback chain: '?' glyph, else zero advance.
const advanceWidth = (str: string, fontData: FontData, scale: number): number => {
  // The FontData index signature is non-optional but lookups can miss —
  // Partial makes that explicit (mirrors the warnMissingGlyphs cast).
  const glyphs = fontData.glyphs as Partial<FontData['glyphs']>
  let w = 0
  for (const ch of Array.from(str)) {
    const glyph = glyphs[ch] ?? glyphs['?']
    if (glyph) w += glyph.ha * scale
  }
  return w
}

// Build the text geometry with intra-block line alignment. Single-line text
// and 'left' keep the native single-TextGeometry path (FontLoader lays out
// multi-line text left-aligned per line already). Other alignments build one
// geometry per line (per word for justify), translate each to its slot, and
// merge into ONE BufferGeometry so the <Center> wrapper, dispose logic and
// single-mesh material path stay unchanged. Vertical parity: line i sits at
// -i * lineHeight3d — byte-identical to FontLoader's native multi-line
// offsets, so switching alignment never shifts the block vertically.
const buildAlignedTextGeometry = (
  text: string,
  align: TextAlign,
  fontData: FontData,
  fontSize: number,
  params: Text3DGeometryParams,
): THREE.BufferGeometry => {
  const lines = text.split('\n')
  if (lines.length === 1 || align === 'left') return new TextGeometry(text, params)

  const scale = fontSize / fontData.resolution
  // FontLoader parity: line_height = (yMax - yMin + underlineThickness) * scale (lineHeight option is 1)
  const lineHeight3d = (fontData.boundingBox.yMax - fontData.boundingBox.yMin + fontData.underlineThickness) * scale
  const widths = lines.map((line) => advanceWidth(line, fontData, scale))
  const maxWidth = Math.max(...widths)

  const parts: TextGeometry[] = []
  lines.forEach((line, i) => {
    const y = -i * lineHeight3d
    if (align === 'justify') {
      const tokens = line.split(' ')
      const gapCount = tokens.length - 1
      if (gapCount === 0) {
        // Single word / empty line — justify falls back to left
        if (line !== '') parts.push(new TextGeometry(line, params).translate(0, y, 0))
        return
      }
      // Stretch each space slot so the line matches the widest line —
      // same gap semantics as computeTextBlockLayout (2D)
      const sumWords = tokens.reduce((sum, token) => sum + advanceWidth(token, fontData, scale), 0)
      const gap = (maxWidth - sumWords) / gapCount
      let x = 0
      for (const token of tokens) {
        if (token !== '') parts.push(new TextGeometry(token, params).translate(x, y, 0))
        x += advanceWidth(token, fontData, scale) + gap
      }
      return
    }
    if (line === '') return
    const offsetX = align === 'center' ? (maxWidth - widths[i]) / 2 : maxWidth - widths[i]
    parts.push(new TextGeometry(line, params).translate(offsetX, y, 0))
  })

  // Whitespace-only text — preserve the native (empty-geometry) behavior
  if (parts.length === 0) return new TextGeometry(text, params)

  const merged = mergeGeometries(parts)
  for (const part of parts) part.dispose()
  return merged ?? new TextGeometry(text, params)
}

// ── Inner mesh — rebuilds only when geometry-affecting props change ─

interface TextGeometryProps {
  text: string
  align: TextAlign
  fontData: FontData
  fontSize: number
  depth: number
  color: string
  unlit: boolean
  metalness: number
  roughness: number
  clearcoat: number
  clearcoatRoughness: number
  reflectivity: number
}

const TextGeometryMesh = ({
  text,
  align,
  fontData,
  fontSize,
  depth,
  color,
  unlit,
  metalness,
  roughness,
  clearcoat,
  clearcoatRoughness,
  reflectivity,
}: TextGeometryProps) => {
  const font = useMemo(
    () => new FontLoader().parse(fontData as unknown as Parameters<FontLoader['parse']>[0]),
    [fontData],
  )

  // Same options drei's <Text3D> passed to three-stdlib's TextGeometry.
  // Built directly (no drei suspense) so a lazily loading font can never
  // suspend the whole Canvas subtree. `bevelSegments` is missing from the
  // published TextGeometryParameters type but supported at runtime (it flows
  // through to ExtrudeGeometry).
  const geometry = useMemo(() => {
    const params: Text3DGeometryParams = {
      font,
      size: fontSize,
      height: depth,
      bevelEnabled: true,
      bevelThickness: 0.3,
      bevelSize: 0.15,
      bevelOffset: 0,
      bevelSegments: 8,
      curveSegments: 32,
      letterSpacing: 0,
      lineHeight: 1,
    }
    return buildAlignedTextGeometry(text, align, fontData, fontSize, params)
  }, [font, fontData, text, align, fontSize, depth])
  useEffect(() => () => geometry.dispose(), [geometry])

  // Under the project's VSM shadow mode (`shadows="variance"`), three.js's
  // WebGLShadowMap auto-includes any `receiveShadow=true` mesh in the shadow
  // render regardless of `castShadow` (VSM needs receiver depth) — so the two
  // fields cannot be controlled independently. We tie both to `!unlit`:
  // lit → text casts AND receives, unlit → it does neither.
  const lit = !unlit

  return (
    <Center cacheKey={geometry.uuid}>
      <mesh geometry={geometry} castShadow={lit} receiveShadow={lit}>
        {unlit ? (
          <meshBasicMaterial color={color} />
        ) : (
          <meshPhysicalMaterial
            color={color}
            metalness={metalness}
            roughness={roughness}
            clearcoat={clearcoat}
            clearcoatRoughness={clearcoatRoughness}
            reflectivity={reflectivity}
          />
        )}
      </mesh>
    </Center>
  )
}

// Missing glyphs are silently skipped by TextGeometry — warn once per
// (fontUrl, missing chars) so the gap is diagnosable. Expected user input
// (e.g. rare kanji outside the CJK subset), so a warn, not reportError.
const warnedMissingGlyphs = new Set<string>()

const warnMissingGlyphs = (fontData: FontData, fontUrl: string, text: string) => {
  const glyphs = fontData.glyphs as Record<string, unknown>
  const missing = [...new Set([...text])].filter((ch) => ch !== '\n' && !glyphs[ch])
  if (missing.length === 0) return
  const key = `${fontUrl}|${missing.join('')}`
  if (warnedMissingGlyphs.has(key)) return
  warnedMissingGlyphs.add(key)
  console.warn(`[text3d] Font ${fontUrl} is missing glyphs, characters will be skipped: ${missing.join(' ')}`)
}

// ── Text3DMeshV2 — positions and rotates the text ─────────────────

interface Text3DMeshV2Props {
  text3d: SceneText3D
}

const Text3DMeshV2 = memo(({text3d}: Text3DMeshV2Props) => {
  const fontUrl = useMemo(
    () => resolveFontUrl(text3d.fontFamily, text3d.bold, text3d.italic),
    [text3d.fontFamily, text3d.bold, text3d.italic],
  )

  // Typeface JSONs load on demand (cached per URL) — the catalog is far too
  // large to preload. `fontData` is derived from the module cache each render;
  // the effect only kicks off a missing download and re-renders once it lands.
  // While loading, the text renders nothing.
  const [, bumpFontVersion] = useState(0)
  const fontData = getLoadedText3DFont(fontUrl)
  useEffect(() => {
    if (getLoadedText3DFont(fontUrl)) return
    let live = true
    loadText3DFont(fontUrl)
      .then(() => {
        if (live) bumpFontVersion((n) => n + 1)
      })
      .catch((err) => reportError(err, 'text3dFont.loadError', {url: fontUrl}))
    return () => {
      live = false
    }
  }, [fontUrl])

  // While the font is loading this text counts as "pending" — render/export
  // paths await zero-pending so a screenshot never misses a mounted text. The
  // cleanup runs AFTER the commit that adds the mesh (or on unmount/failure),
  // releasing the waiters only once the text is actually in the scene.
  useEffect(() => {
    if (fontData) return
    return trackText3DFontPending()
  }, [fontData])

  useEffect(() => {
    if (fontData) warnMissingGlyphs(fontData, fontUrl, text3d.text)
  }, [fontData, fontUrl, text3d.text])

  const rotation = useMemo(
    (): [number, number, number] => [
      THREE.MathUtils.degToRad(text3d.rotationX),
      THREE.MathUtils.degToRad(text3d.rotationY),
      THREE.MathUtils.degToRad(text3d.rotationZ),
    ],
    [text3d.rotationX, text3d.rotationY, text3d.rotationZ],
  )

  if (!fontData) return null

  return (
    <group position={[text3d.x, text3d.y, text3d.z]} rotation={rotation}>
      {/* `key` forces a fresh TextGeometryMesh subtree every time the unlit
          checkbox toggles — guarantees the material swap takes effect and that
          the new meshes are constructed in a state consistent with the current
          prop. */}
      <TextGeometryMesh
        key={text3d.unlit ? 'unlit' : 'lit'}
        text={text3d.text}
        align={text3d.align ?? 'center'}
        fontData={fontData}
        fontSize={text3d.fontSize}
        depth={text3d.depth}
        color={text3d.color}
        unlit={text3d.unlit}
        metalness={text3d.material.metalness}
        roughness={text3d.material.roughness}
        clearcoat={text3d.material.clearcoat}
        clearcoatRoughness={text3d.material.clearcoatRoughness}
        reflectivity={text3d.material.reflectivity}
      />
    </group>
  )
})
Text3DMeshV2.displayName = 'Text3DMeshV2'

export default Text3DMeshV2
